import { DragItem } from '@/contexts/DragDropContext';

export interface ExpressionGeneratorOptions {
  nodeId: string;
  nodeName: string;
  path: string;
  fieldType: string;
  targetType?: string;
}

export class ExpressionGenerator {
  /**
   * Convert a path to use bracket notation for property names that require it
   * (e.g., names with spaces, special characters, or starting with numbers)
   * Example: "childOutputs.AI Agent.data" -> "childOutputs[\"AI Agent\"].data"
   */
  static convertPathToBracketNotation(path: string): string {
    if (!path) return path;

    // Parse the path into segments, respecting existing bracket notation
    const result: string[] = [];
    let i = 0;

    while (i < path.length) {
      // Skip leading dots
      if (path[i] === '.') {
        i++;
        continue;
      }

      // Handle existing bracket notation
      if (path[i] === '[') {
        const bracketEnd = path.indexOf(']', i);
        if (bracketEnd !== -1) {
          result.push(path.substring(i, bracketEnd + 1));
          i = bracketEnd + 1;
          continue;
        }
      }

      // Read property name until dot or bracket
      const start = i;
      while (i < path.length && path[i] !== '.' && path[i] !== '[') {
        i++;
      }

      const propName = path.substring(start, i);
      if (propName) {
        // Check if property name needs bracket notation
        // (contains spaces, special chars, or starts with number)
        const needsBrackets = /\s|[^\w]|^\d/.test(propName);
        if (needsBrackets) {
          result.push(`["${propName}"]`);
        } else {
          result.push(propName);
        }
      }
    }

    // Join with dots, but don't add dot before bracket notation
    let output = '';
    for (let j = 0; j < result.length; j++) {
      const segment = result[j];
      if (j === 0) {
        output = segment;
      } else if (segment.startsWith('[')) {
        output += segment;
      } else {
        output += '.' + segment;
      }
    }

    return output;
  }

  /**
   * Generate an expression from a dragged schema field
   */
  static generate(item: DragItem): string {
    const { nodeName, path, isVirtualItem } = item;

    // Clean up path - remove node name prefix if exists
    let cleanPath = path;
    const nodeNamePrefix = `${nodeName}.`;
    if (cleanPath.startsWith(nodeNamePrefix)) {
      cleanPath = cleanPath.substring(nodeNamePrefix.length);
    }

    // Remove 'output' prefix if it exists (from unified format in tree view)
    // The tree shows { success, output, logs, metrics } - strip 'output' since the
    // expression syntax $node["Name"].output already navigates to the output value.
    // Remove 'output' prefix if it exists (from schema inference)
    // This handles: output.data, output[0], and output (the root output itself)
    if (cleanPath === 'output') {
      // Path is exactly 'output' - user dragged the entire output
      // Return just the node reference to avoid duplicate .output.output
      cleanPath = '';
    } else if (cleanPath.startsWith('output.')) {
      cleanPath = cleanPath.substring('output.'.length);
    } else if (cleanPath.startsWith('output[')) {
      // For array outputs, remove just 'output' and keep the bracket
      cleanPath = cleanPath.substring('output'.length);
    }

    // Convert path to proper bracket notation for property names with spaces
    cleanPath = this.convertPathToBracketNotation(cleanPath);

    // For virtual items (current item in iterating nodes), generate .output.item.field
    // This allows iterating nodes to reference the current item being processed
    if (isVirtualItem) {
      // cleanPath is the field path within the item (e.g., "id", "name", "nested.field")
      return `{{$node["${nodeName}"].output.item${cleanPath ? (cleanPath.startsWith('[') ? '' : '.') + cleanPath : ''}}}`;
    }

    // Generate standard node expression
    // Use .output to access the actual data in the standard schema
    // The path already includes array notation if needed (e.g., [0].field)
    if (!cleanPath) {
      // No path - return just the output reference
      return `{{$node["${nodeName}"].output}}`;
    }
    return `{{$node["${nodeName}"].output${cleanPath.startsWith('[') ? '' : '.'}${cleanPath}}}`;
  }

  /**
   * Generate a human-readable reference
   */
  static generateReference(item: DragItem): string {
    const { nodeName, path } = item;
    return `${nodeName}.${path}`;
  }

  /**
   * Validate if a field type is compatible with target type
   */
  static isTypeCompatible(sourceType: string, targetType?: string): boolean {
    if (!targetType) return true; // No target type means accept anything

    // Exact match
    if (sourceType === targetType) return true;

    // Compatible types
    const compatibilityMap: Record<string, string[]> = {
      string: ['string', 'text', 'any'],
      number: ['number', 'integer', 'float', 'any'],
      boolean: ['boolean', 'any'],
      object: ['object', 'json', 'any'],
      array: ['array', 'list', 'any'],
      null: ['any'],
    };

    const compatibleTypes = compatibilityMap[sourceType] || [];
    return compatibleTypes.includes(targetType);
  }

  /**
   * Extract node ID from an expression
   */
  static extractNodeId(expression: string): string | null {
    const match = expression.match(/\{\{\$node\.([^.]+)\./);
    return match ? match[1] : null;
  }

  /**
   * Extract path from an expression
   */
  static extractPath(expression: string): string | null {
    const match = expression.match(/\{\{\$node\.[^.]+\.(.+)\}\}/);
    return match ? match[1] : null;
  }

  /**
   * Check if a string is a valid expression
   */
  static isExpression(value: string): boolean {
    return /^\{\{\$node\.[^.]+\..+\}\}$/.test(value);
  }

  /**
   * Parse an expression into its components
   */
  static parseExpression(expression: string): {
    nodeId: string;
    path: string;
  } | null {
    const nodeId = this.extractNodeId(expression);
    const path = this.extractPath(expression);

    if (!nodeId || !path) return null;

    return { nodeId, path };
  }

  /**
   * Format a path for display (e.g., "data.user.name" -> "data → user → name")
   */
  static formatPath(path: string): string {
    return path.split('.').join(' → ');
  }

  /**
   * Get type icon for a field type
   */
  static getTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
      string: '📝',
      number: '🔢',
      boolean: '✓',
      object: '{}',
      array: '[]',
      null: '∅',
    };
    return iconMap[type] || '•';
  }

  /**
   * Get type color for a field type
   */
  static getTypeColor(type: string): string {
    const colorMap: Record<string, string> = {
      string: 'text-blue-600',
      number: 'text-green-600',
      boolean: 'text-purple-600',
      object: 'text-orange-600',
      array: 'text-pink-600',
      null: 'text-gray-400',
    };
    return colorMap[type] || 'text-gray-600';
  }

}
